<template>
  <div class="w-full">
    <div class="w-[90%] max-w-5xl mx-auto py-14">
      <h1 class="text-4xl font-semibold mb-2">Bunce CRM</h1>
      <p class="text-grey text-sm mb-8">Manage segments and export data to Bunce</p>

      <!-- Tabs -->
      <div class="flex gap-1 mb-8 border-b border-neutral-200">
        <button
          v-for="tab in tabs"
          :key="tab.key"
          @click="activeTab = tab.key"
          class="px-5 py-2.5 text-sm font-medium transition-colors"
          :class="activeTab === tab.key ? 'border-b-2 border-black text-black -mb-px' : 'text-grey hover:text-black'"
        >
          {{ tab.label }}
        </button>
      </div>

      <!-- ── TAB: Segments ─────────────────────────────────────────────── -->
      <div v-if="activeTab === 'segments'">

        <!-- Segment detail view -->
        <div v-if="activeSegment" class="w-full pb-20">
          <div class="w-full mt-2 mb-8 flex items-center justify-between">
            <span class="cursor-pointer underline text-sm" @click="closeSegmentDetail">← Back to segments</span>
          </div>

          <h2 class="text-2xl font-semibold mb-1">{{ activeSegment.name }}</h2>
          <p class="text-grey text-sm mb-6">{{ activeSegment.description || 'No description' }}</p>

          <div class="flex gap-6 mb-8 text-sm">
            <div>
              <span class="text-grey">Type</span>
              <div class="font-medium mt-0.5 capitalize">{{ activeSegment.type }}</div>
            </div>
            <div>
              <span class="text-grey">Customers (Bunce count)</span>
              <div class="font-medium mt-0.5">{{ activeSegment.customers_count ?? '—' }}</div>
            </div>
            <div>
              <span class="text-grey">Created</span>
              <div class="font-medium mt-0.5">{{ $store.getters.formatDate(activeSegment.created_at) }}</div>
            </div>
          </div>

          <div class="flex items-center justify-between mb-4">
            <h3 class="text-lg font-semibold">Exported Records</h3>
            <span class="text-sm text-grey">{{ segmentCustomers.length }} total from our DB</span>
          </div>

          <div v-if="loadingCustomers" class="pt-20 flex justify-center"><spinner /></div>

          <div v-else-if="!segmentCustomers.length" class="py-12 text-center text-grey text-sm border border-neutral-200 rounded-lg">
            No records have been exported to this segment yet.
          </div>

          <div v-else class="overflow-x-auto">
            <table class="w-full text-sm border border-neutral-200 rounded-lg overflow-hidden">
              <thead class="bg-neutral-50 text-left">
                <tr>
                  <th class="px-4 py-3 font-medium text-grey">Name</th>
                  <th class="px-4 py-3 font-medium text-grey">Email</th>
                  <th class="px-4 py-3 font-medium text-grey">Phone</th>
                  <th class="px-4 py-3 font-medium text-grey">Source</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(c, i) in segmentCustomers"
                  :key="i"
                  class="border-t border-neutral-100 hover:bg-neutral-50"
                >
                  <td class="px-4 py-3">{{ [c.first_name, c.last_name].filter(Boolean).join(' ') || '—' }}</td>
                  <td class="px-4 py-3">{{ c.email || '—' }}</td>
                  <td class="px-4 py-3">{{ c.phone_no || '—' }}</td>
                  <td class="px-4 py-3 text-grey text-xs">{{ c._source }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Segments list view -->
        <div v-else>
          <div class="flex items-center justify-between mb-6">
            <span class="text-sm">{{ segments.length }} segment{{ segments.length === 1 ? '' : 's' }}</span>
            <button
              @click="openCreateModal"
              class="bg-black text-white text-sm px-4 py-2 rounded hover:bg-neutral-800 transition-colors"
            >
              + Create Segment
            </button>
          </div>

          <div v-if="loadingSegments" class="pt-20 flex justify-center"><spinner /></div>

          <div v-else-if="!segments.length" class="py-16 text-center text-grey text-sm border border-neutral-200 rounded-lg">
            No segments yet. Create one to get started.
          </div>

          <div v-else class="space-y-3">
            <div
              v-for="seg in segments"
              :key="seg.id"
              class="border border-neutral-200 rounded-lg p-4 flex items-center justify-between gap-4 cursor-pointer hover:bg-neutral-50 transition-colors"
              @click="openSegmentDetail(seg)"
            >
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2 mb-1">
                  <span class="font-medium">{{ seg.name }}</span>
                  <span class="text-xs bg-neutral-100 text-neutral-500 px-2 py-0.5 rounded capitalize">{{ seg.type }}</span>
                </div>
                <p class="text-xs text-grey">{{ seg.description || 'No description' }}</p>
              </div>
              <div class="text-right shrink-0">
                <div class="text-sm font-medium">{{ seg.all_customers ?? 0 }} customers</div>
                <div class="text-xs text-grey mt-0.5">{{ $store.getters.formatDate(seg.created_at) }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ── TAB: Active Exports ───────────────────────────────────────── -->
      <div v-if="activeTab === 'jobs'">
        <div class="flex items-center justify-between mb-6">
          <span class="text-sm">{{ exportJobs.length }} export job{{ exportJobs.length === 1 ? '' : 's' }}</span>
        </div>

        <div v-if="loadingJobs" class="pt-20 flex justify-center"><spinner /></div>

        <div v-else-if="!exportJobs.length" class="py-16 text-center text-grey text-sm border border-neutral-200 rounded-lg">
          No export jobs yet. Initiate an export from the Export tab to start auto-sync.
        </div>

        <div v-else class="overflow-x-auto">
          <table class="w-full text-sm border border-neutral-200 rounded-lg overflow-hidden">
            <thead class="bg-neutral-50 text-left">
              <tr>
                <th class="px-4 py-3 font-medium text-grey">Segment</th>
                <th class="px-4 py-3 font-medium text-grey">Source</th>
                <th class="px-4 py-3 font-medium text-grey">Total Exported</th>
                <th class="px-4 py-3 font-medium text-grey">Last Checked</th>
                <th class="px-4 py-3 font-medium text-grey">Last Updated</th>
                <th class="px-4 py-3 font-medium text-grey">Status</th>
                <th class="px-4 py-3 font-medium text-grey">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="job in exportJobs" :key="job.id" class="border-t border-neutral-100 hover:bg-neutral-50">
                <td class="px-4 py-3 font-medium">{{ job.segment_name }}</td>
                <td class="px-4 py-3 text-grey">{{ job.source_label }}</td>
                <td class="px-4 py-3">{{ job.total_exported }}</td>
                <td class="px-4 py-3 text-grey text-xs">{{ job.last_checked_at ? $store.getters.formatDate(job.last_checked_at) : '—' }}</td>
                <td class="px-4 py-3 text-grey text-xs">{{ job.last_updated_at ? $store.getters.formatDate(job.last_updated_at) : '—' }}</td>
                <td class="px-4 py-3">
                  <span class="text-xs px-2 py-0.5 rounded font-medium" :class="job.is_active ? 'bg-green-100 text-green-700' : 'bg-neutral-100 text-neutral-500'">
                    {{ job.is_active ? 'Active' : 'Paused' }}
                  </span>
                </td>
                <td class="px-4 py-3">
                  <div class="flex gap-2">
                    <button
                      @click="runJobNow(job)"
                      :disabled="job._running"
                      class="text-xs border border-neutral-200 px-2.5 py-1 rounded hover:bg-neutral-50 transition-colors disabled:opacity-50"
                    >
                      {{ job._running ? 'Running…' : 'Run Now' }}
                    </button>
                    <button
                      @click="toggleJob(job)"
                      :disabled="job._toggling"
                      class="text-xs border px-2.5 py-1 rounded transition-colors disabled:opacity-50"
                      :class="job.is_active ? 'border-red-200 text-red-600 hover:bg-red-50' : 'border-green-200 text-green-700 hover:bg-green-50'"
                    >
                      {{ job._toggling ? '…' : (job.is_active ? 'Pause' : 'Resume') }}
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- ── TAB: Export ───────────────────────────────────────────────── -->
      <div v-if="activeTab === 'export'" class="max-w-xl">
        <p class="text-sm text-grey mb-8">
          Select a segment and a data source to export records from your database to Bunce.
          Only records not yet in the selected segment will be exported.
          Once initiated, the export will auto-sync on the configured schedule.
        </p>

        <!-- Segment dropdown -->
        <label class="block mb-5">
          <span class="text-sm font-medium text-grey">Segment</span>
          <select
            v-model="exportForm.segmentId"
            class="mt-1 w-full px-3 py-2 border border-grey border-opacity-35 rounded-md text-sm"
          >
            <option value="">— Select a segment —</option>
            <option v-for="seg in segments" :key="seg.id" :value="seg.id">{{ seg.name }}</option>
          </select>
        </label>

        <!-- Source dropdown -->
        <label class="block mb-8">
          <span class="text-sm font-medium text-grey">Data Source</span>
          <select
            v-model="exportForm.sourceId"
            class="mt-1 w-full px-3 py-2 border border-grey border-opacity-35 rounded-md text-sm"
          >
            <option value="">— Select a data source —</option>
            <option v-for="src in sources" :key="src.id" :value="src.id">{{ src.label }}</option>
          </select>
        </label>

        <!-- Export button -->
        <button
          @click="runExport"
          :disabled="!exportForm.segmentId || !exportForm.sourceId || exporting"
          class="bg-black text-white text-sm px-6 py-2.5 rounded hover:bg-neutral-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {{ exporting ? 'Exporting...' : 'Export to Bunce' }}
        </button>


      </div>
    </div>

    <!-- ── Create Segment Modal ──────────────────────────────────────────── -->
    <div
      v-if="showCreateModal"
      class="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4"
      @click.self="closeCreateModal"
    >
      <div class="bg-white rounded-xl w-full max-w-md p-6">
        <h2 class="text-xl font-semibold mb-5">Create Segment</h2>

        <label class="block mb-4">
          <span class="text-sm font-medium text-grey">Name <span class="text-red-500">*</span></span>
          <input
            v-model="createForm.name"
            type="text"
            placeholder="e.g. DODO Waitlist"
            class="mt-1 w-full px-3 py-2 border border-grey border-opacity-35 rounded-md text-sm"
          />
        </label>

        <label class="block mb-6">
          <span class="text-sm font-medium text-grey">Description</span>
          <textarea
            v-model="createForm.description"
            rows="3"
            placeholder="Optional description..."
            class="mt-1 w-full px-3 py-2 border border-grey border-opacity-35 rounded-md text-sm resize-none"
          />
        </label>

        <div class="flex gap-3 justify-end">
          <button
            @click="closeCreateModal"
            class="text-sm px-4 py-2 border border-neutral-200 rounded hover:bg-neutral-50 transition-colors"
          >
            Cancel
          </button>
          <button
            @click="submitCreateSegment"
            :disabled="!createForm.name || creating"
            class="text-sm bg-black text-white px-4 py-2 rounded hover:bg-neutral-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {{ creating ? 'Creating...' : 'Create' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'BuncePage',
  data() {
    return {
      activeTab: 'segments',
      tabs: [
        { key: 'segments', label: 'Segments' },
        { key: 'jobs', label: 'Active Exports' },
        { key: 'export', label: 'Export' },
      ],

      // Segments list
      segments: [],
      loadingSegments: false,

      // Segment detail
      activeSegment: null,
      segmentCustomers: [],
      loadingCustomers: false,

      // Sources
      sources: [],

      // Export jobs
      exportJobs: [],
      loadingJobs: false,

      // Create modal
      showCreateModal: false,
      createForm: { name: '', description: '' },
      creating: false,

      // Export
      exportForm: { segmentId: '', sourceId: '' },
      exporting: false,
    };
  },
  created() {
    this.fetchSegments();
    this.fetchSources();
    this.fetchExportJobs();
  },
  methods: {
    // ── Fetch ──────────────────────────────────────────────────────────────
    async fetchSegments() {
      this.loadingSegments = true;
      try {
        const res = await this.$store.dispatch('getRequest', {
          path: '/admin/api/bunce/segments',
        });
        this.segments = res.data?.data || [];
      } catch (e) {
        this.$toast.error(e?.response?.data?.error || 'Failed to load segments');
      } finally {
        this.loadingSegments = false;
      }
    },

    async fetchSources() {
      try {
        const res = await this.$store.dispatch('getRequest', {
          path: '/admin/api/bunce/sources',
        });
        this.sources = res.data?.data || [];
      } catch (e) {
        this.$toast.error(e?.response?.data?.error || 'Failed to load data sources');
      }
    },

    async fetchSegmentCustomers(segmentId) {
      this.loadingCustomers = true;
      this.segmentCustomers = [];
      try {
        const res = await this.$store.dispatch('getRequest', {
          path: `/admin/api/bunce/segments/${segmentId}/customers`,
        });
        this.segmentCustomers = res.data?.customers || [];
        // Merge Bunce metadata (customers_count) into activeSegment
        if (res.data?.segment) {
          this.activeSegment = { ...this.activeSegment, ...res.data.segment };
        }
      } catch (e) {
        this.$toast.error(e?.response?.data?.error || 'Failed to load segment customers');
      } finally {
        this.loadingCustomers = false;
      }
    },

    // ── Segment detail ─────────────────────────────────────────────────────
    openSegmentDetail(seg) {
      this.activeSegment = seg;
      this.fetchSegmentCustomers(seg.id);
    },

    closeSegmentDetail() {
      this.activeSegment = null;
      this.segmentCustomers = [];
    },

    // ── Create modal ───────────────────────────────────────────────────────
    openCreateModal() {
      this.createForm = { name: '', description: '' };
      this.showCreateModal = true;
    },

    closeCreateModal() {
      this.showCreateModal = false;
    },

    async submitCreateSegment() {
      if (!this.createForm.name) return;
      this.creating = true;
      try {
        const res = await this.$store.dispatch('postRequest', {
          path: '/admin/api/bunce/segments',
          data: this.createForm,
        });
        this.segments.unshift(res.data?.data);
        this.$toast.success(`Segment "${this.createForm.name}" created successfully`);
        this.closeCreateModal();
      } catch (e) {
        this.$toast.error(e?.response?.data?.error || 'Failed to create segment');
      } finally {
        this.creating = false;
      }
    },

    // ── Export Jobs ────────────────────────────────────────────────────────
    async fetchExportJobs() {
      this.loadingJobs = true;
      try {
        const res = await this.$store.dispatch('getRequest', { path: '/admin/api/bunce/export-jobs' });
        this.exportJobs = (res.data?.data || []).map(j => ({ ...j, _running: false, _toggling: false }));
      } catch (e) {
        this.$toast.error(e?.response?.data?.error || 'Failed to load export jobs');
      } finally {
        this.loadingJobs = false;
      }
    },

    async runJobNow(job) {
      job._running = true;
      try {
        const res = await this.$store.dispatch('postRequest', {
          path: `/admin/api/bunce/export-jobs/${job.id}/run`,
          data: {},
        });
        const { exported } = res.data;
        this.$toast.success(exported > 0 ? `${exported} new record(s) exported.` : 'No new records found.');
        await this.fetchExportJobs();
      } catch (e) {
        this.$toast.error(e?.response?.data?.error || 'Failed to run job');
        job._running = false;
      }
    },

    async toggleJob(job) {
      job._toggling = true;
      const wasActive = job.is_active;
      try {
        await this.$store.dispatch('patchRequest', {
          path: `/admin/api/bunce/export-jobs/${job.id}/toggle`,
          data: {},
        });
        this.$toast.success(`Job ${wasActive ? 'paused' : 'resumed'}.`);
        await this.fetchExportJobs();
      } catch (e) {
        this.$toast.error(e?.response?.data?.error || 'Failed to toggle job');
        job._toggling = false;
      }
    },

    // ── Export ─────────────────────────────────────────────────────────────
    async runExport() {
      if (!this.exportForm.segmentId || !this.exportForm.sourceId) return;
      this.exporting = true;
      try {
        const res = await this.$store.dispatch('postRequest', {
          path: '/admin/api/bunce/export',
          data: this.exportForm,
        });
        const { exported, alreadyExported } = res.data;
        this.$toast.success(
          `Export complete — ${exported} new record${exported === 1 ? '' : 's'} exported, ${alreadyExported} already in segment`
        );
        this.fetchSegments();
        this.fetchExportJobs();
        this.activeTab = 'jobs';
      } catch (e) {
        this.$toast.error(e?.response?.data?.error || 'Export failed');
      } finally {
        this.exporting = false;
      }
    },
  },
};
</script>
